import React, { Component } from "react";
import { Row, Col, Card, Spinner, Table, Badge, ButtonGroup, Button, Form } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

// Predictive & longitudinal analytics for a single student.
// Backs cluster-1 requirements: rank movement over time, percentile standing,
// predicted NEET/JEE score, All-India-rank simulation, subject-wise marks trend,
// and an auto-generated revision planner.
const PREDICTIVE_QUERY = gql`
  query (
    $mobile: String!
    $exam_type: Int
    $target: String
    $bucket: String
    $fromDate: String
    $toDate: String
  ) {
    getStudentPredictiveAnalytics(
      mobile: $mobile
      exam_type: $exam_type
      target: $target
      bucket: $bucket
      fromDate: $fromDate
      toDate: $toDate
    ) {
      rankTrend {
        period
        label
        examCount
        avgRank
        bestRank
        avgScore
        avgPercentile
      }
      percentileStanding {
        examsConsidered
        sectionPercentile
        branchPercentile
        platformPercentile
        sectionSize
        branchSize
      }
      subjectTrend {
        subject_id
        subject
        points {
          period
          label
          avgScore
          accuracy
        }
      }
      prediction {
        target
        maxScore
        examsAnalyzed
        currentAvgScore
        trajectorySlope
        trend
        predictedScore
        projectedAccuracy
        platformPercentile
        projectedPercentile
        simulatedAIR
        projectedAIR
        cutoffScore
        meetsCutoff
        message
      }
      revisionPlanner {
        subject_id
        subject
        weakChapterCount
        chapters {
          chapter_id
          chapter
          accuracy
          attempted
          priority
        }
      }
    }
  }
`;

const PRIORITY_VARIANT = { High: "danger", Medium: "warning", Low: "info" };
const TREND_VARIANT = { improving: "success", declining: "danger", stable: "secondary" };
const EXAM_TYPES = [
    { value: 0, label: "All Exams" },
    { value: 1, label: "Mock Test" },
    { value: 2, label: "Custom Exam" },
    { value: 3, label: "Scheduled Exam" },
    { value: 6, label: "Practice Test" },
    { value: 7, label: "Adaptive Test" },
];

class StudentPredictiveAnalytics extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            data: null,
            error: null,
            target: "neet",
            examType: 0,
            bucket: "month",
        };
    }

    componentDidMount() {
        this.fetch();
    }

    componentDidUpdate(prevProps) {
        // Re-fetch when the student or an externally-supplied date window changes.
        if (
            prevProps.mobile !== this.props.mobile ||
            prevProps.fromDate !== this.props.fromDate ||
            prevProps.toDate !== this.props.toDate
        ) {
            this.fetch();
        }
    }

    fetch = async () => {
        const { mobile, fromDate, toDate, client } = this.props;
        if (!mobile) return;
        this.setState({ loading: true, error: null });
        try {
            const res = await client.query({
                query: PREDICTIVE_QUERY,
                variables: {
                    mobile: String(mobile),
                    exam_type: Number(this.state.examType) || 0,
                    target: this.state.target,
                    bucket: this.state.bucket,
                    fromDate: fromDate != null ? String(fromDate) : null,
                    toDate: toDate != null ? String(toDate) : null,
                },
                fetchPolicy: "network-only",
            });
            this.setState({ data: res?.data?.getStudentPredictiveAnalytics || null, loading: false });
        } catch (e) {
            this.setState({ error: e.message || "Failed to load predictive analytics", loading: false });
        }
    };

    setTarget = (target) => {
        if (target === this.state.target) return;
        this.setState({ target }, this.fetch);
    };

    setExamType = (e) => {
        this.setState({ examType: Number(e.target.value) }, this.fetch);
    };

    setBucket = (bucket) => {
        if (bucket === this.state.bucket) return;
        this.setState({ bucket }, this.fetch);
    };

    rankTrendOptions = (rankTrend) => {
        const categories = rankTrend.map((p) => p.label);
        return {
            chart: { height: 260, type: "line" },
            title: { text: null },
            credits: { enabled: false },
            xAxis: { categories },
            yAxis: [
                { title: { text: "Avg Rank" }, reversed: true, allowDecimals: false },
                { title: { text: "Percentile" }, min: 0, max: 100, opposite: true },
            ],
            tooltip: { shared: true },
            series: [
                {
                    name: "Avg Rank",
                    data: rankTrend.map((p) => (p.avgRank != null ? Number(p.avgRank) : null)),
                    color: "#077EE6",
                    yAxis: 0,
                },
                {
                    name: "Best Rank",
                    data: rankTrend.map((p) => (p.bestRank != null ? Number(p.bestRank) : null)),
                    color: "#27AE60",
                    dashStyle: "ShortDash",
                    yAxis: 0,
                },
                {
                    name: "Percentile",
                    data: rankTrend.map((p) => (p.avgPercentile != null ? Number(p.avgPercentile) : null)),
                    color: "#F2994A",
                    yAxis: 1,
                },
            ],
        };
    };

    subjectTrendOptions = (subjectTrend) => {
        // Align all subjects onto a single, time-ordered category axis.
        const labelSet = [];
        subjectTrend.forEach((s) =>
            (s.points || []).forEach((p) => {
                if (!labelSet.includes(p.label)) labelSet.push(p.label);
            })
        );
        const colors = ["#077EE6", "#27AE60", "#F2994A", "#9B51E0", "#EB5757", "#2D9CDB"];
        return {
            chart: { height: 260, type: "line" },
            title: { text: null },
            credits: { enabled: false },
            xAxis: { categories: labelSet },
            yAxis: { title: { text: "Avg Subject Score" } },
            tooltip: { shared: true },
            series: subjectTrend.map((s, i) => {
                const byLabel = {};
                (s.points || []).forEach((p) => { byLabel[p.label] = p.avgScore; });
                return {
                    name: s.subject,
                    color: colors[i % colors.length],
                    data: labelSet.map((l) => (byLabel[l] != null ? Number(byLabel[l]) : null)),
                };
            }),
        };
    };

    renderPrediction(prediction) {
        if (!prediction) {
            return <p className="text-muted mb-0">Not enough full-length attempts yet to project a score.</p>;
        }
        return (
            <Row>
                <Col md={3} sm={6} className="mb-2">
                    <div className="text-muted small">Predicted {prediction.target} Score</div>
                    <h3 className="mb-0">
                        {prediction.predictedScore}
                        <small className="text-muted">/{prediction.maxScore}</small>
                    </h3>
                    <Badge variant={TREND_VARIANT[prediction.trend] || "secondary"}>{prediction.trend}</Badge>{" "}
                    <span className="small text-muted">
                        {prediction.trajectorySlope >= 0 ? "+" : ""}
                        {prediction.trajectorySlope} pts/test
                    </span>
                </Col>
                <Col md={3} sm={6} className="mb-2">
                    <div className="text-muted small">Current Avg (scaled)</div>
                    <h3 className="mb-0">
                        {prediction.currentAvgScore}
                        <small className="text-muted">/{prediction.maxScore}</small>
                    </h3>
                    <span className="small text-muted">{prediction.examsAnalyzed} attempts analysed</span>
                </Col>
                <Col md={3} sm={6} className="mb-2">
                    <div className="text-muted small">Simulated All-India Rank</div>
                    <h3 className="mb-0">{Number(prediction.projectedAIR).toLocaleString()}</h3>
                    <span className="small text-muted">
                        projected percentile {prediction.projectedPercentile}
                    </span>
                </Col>
                <Col md={3} sm={6} className="mb-2">
                    <div className="text-muted small">Target Cutoff ({prediction.cutoffScore})</div>
                    <h3 className="mb-0">
                        <Badge variant={prediction.meetsCutoff ? "success" : "danger"}>
                            {prediction.meetsCutoff ? "On Track" : "Below Cutoff"}
                        </Badge>
                    </h3>
                </Col>
                <Col xs={12}>
                    <p className="small text-muted mb-0 mt-1">{prediction.message}</p>
                </Col>
            </Row>
        );
    }

    renderPercentile(ps) {
        if (!ps) return null;
        const stat = (label, val, sub) => (
            <Col md={4} sm={12} className="mb-2">
                <div className="text-muted small">{label}</div>
                <h4 className="mb-0">{val != null ? `${val}%` : "—"}</h4>
                {sub ? <span className="small text-muted">{sub}</span> : null}
            </Col>
        );
        return (
            <Row>
                {stat("Section Percentile", ps.sectionPercentile, `of ${ps.sectionSize} students`)}
                {stat("Branch Percentile", ps.branchPercentile, `of ${ps.branchSize} students`)}
                {stat("Platform Percentile", ps.platformPercentile, `${ps.examsConsidered} ranked exams`)}
            </Row>
        );
    }

    renderRevisionPlanner(planner) {
        if (!planner || !planner.length) {
            return <p className="text-muted mb-0">No weak chapters detected — keep it up!</p>;
        }
        return planner.map((subj) => (
            <div key={subj.subject_id} className="mb-3">
                <h6 className="mb-1">
                    {subj.subject}{" "}
                    <Badge variant="light">{subj.weakChapterCount} weak chapters</Badge>
                </h6>
                <Table size="sm" bordered responsive className="mb-0">
                    <thead>
                        <tr>
                            <th>Chapter</th>
                            <th className="text-center">Accuracy</th>
                            <th className="text-center">Attempted</th>
                            <th className="text-center">Priority</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subj.chapters.map((c, idx) => (
                            <tr key={c.chapter_id != null ? c.chapter_id : `c-${idx}`}>
                                <td>{c.chapter}</td>
                                <td className="text-center">{c.accuracy}%</td>
                                <td className="text-center">{c.attempted}</td>
                                <td className="text-center">
                                    <Badge variant={PRIORITY_VARIANT[c.priority] || "secondary"}>
                                        {c.priority}
                                    </Badge>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        ));
    }

    render() {
        const { loading, data, error, target } = this.state;

        const Section = ({ title, children }) => (
            <Card className="mb-3">
                <Card.Body>
                    <h6 className="font-weight-bold mb-3">{title}</h6>
                    {children}
                </Card.Body>
            </Card>
        );

        return (
            <div className="studentPredictiveAnalytics mt-3">
                <Row className="align-items-center mb-3">
                    <Col xs={12} md>
                        <h5 className="mb-2 mb-md-0 font-weight-bold">Predictive &amp; Longitudinal Analytics</h5>
                    </Col>
                    <Col xs="auto" className="mb-1">
                        <Form.Control
                            as="select"
                            size="sm"
                            value={this.state.examType}
                            onChange={this.setExamType}
                            style={{ width: "160px" }}
                        >
                            {EXAM_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </Form.Control>
                    </Col>
                    <Col xs="auto" className="mb-1">
                        <ButtonGroup size="sm">
                            <Button
                                variant={this.state.bucket === "month" ? "secondary" : "outline-secondary"}
                                onClick={() => this.setBucket("month")}
                            >
                                Monthly
                            </Button>
                            <Button
                                variant={this.state.bucket === "week" ? "secondary" : "outline-secondary"}
                                onClick={() => this.setBucket("week")}
                            >
                                Weekly
                            </Button>
                        </ButtonGroup>
                    </Col>
                    <Col xs="auto" className="mb-1">
                        <ButtonGroup size="sm">
                            <Button
                                variant={target === "neet" ? "primary" : "outline-primary"}
                                onClick={() => this.setTarget("neet")}
                            >
                                NEET
                            </Button>
                            <Button
                                variant={target === "jee" ? "primary" : "outline-primary"}
                                onClick={() => this.setTarget("jee")}
                            >
                                JEE
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : error ? (
                    <Card className="mb-3">
                        <Card.Body>
                            <span className="text-danger">{error}</span>
                        </Card.Body>
                    </Card>
                ) : !data ? (
                    <Card className="mb-3">
                        <Card.Body>
                            <span className="text-muted">No analytics available for this student / filter.</span>
                        </Card.Body>
                    </Card>
                ) : (
                    <>
                        <Section title={`Score Prediction & All-India Rank Simulation (${(data.prediction && data.prediction.target) || target.toUpperCase()})`}>
                            {this.renderPrediction(data.prediction)}
                        </Section>

                        <Section title="Percentile Standing">
                            {this.renderPercentile(data.percentileStanding)}
                        </Section>

                        <Row>
                            <Col lg={6}>
                                <Section title="Rank Movement Over Time">
                                    {data.rankTrend && data.rankTrend.length ? (
                                        <HighchartsReact
                                            highcharts={Highcharts}
                                            options={this.rankTrendOptions(data.rankTrend)}
                                        />
                                    ) : (
                                        <p className="text-muted mb-0">No ranked exams in this period.</p>
                                    )}
                                </Section>
                            </Col>
                            <Col lg={6}>
                                <Section title="Subject-wise Marks Trend">
                                    {data.subjectTrend && data.subjectTrend.length ? (
                                        <HighchartsReact
                                            highcharts={Highcharts}
                                            options={this.subjectTrendOptions(data.subjectTrend)}
                                        />
                                    ) : (
                                        <p className="text-muted mb-0">No subject data in this period.</p>
                                    )}
                                </Section>
                            </Col>
                        </Row>

                        <Section title="Auto Revision Planner (weak chapters)">
                            {this.renderRevisionPlanner(data.revisionPlanner)}
                        </Section>
                    </>
                )}
            </div>
        );
    }
}

export default withApollo(StudentPredictiveAnalytics);
